import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { CauseRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  Cause_Bool_Exp,
  GetCausesQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetCausesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useCausesGraphqlVariables = () => {
  const whereFilter = useEntityWhereFilter<Cause_Bool_Exp>(
    Parent_Type_Enum.Cause
  );

  return { where: whereFilter };
};

type InternalArgs = {
  entityIds?: string[];
};

type UseGetCauseRegisterArgs = Record<string, never>;

/**
 * Maps TRPC cause data to match the GraphQL query structure
 */
export const mapTrpcCausesToGraphQL = (
  trpcData: CauseRegisterResponseRow[]
): StrictMappedGraphQLSelection<GetCausesQuery> => {
  return {
    cause: trpcData,
  };
};

const useGetCauseRegisterBase = createQueryHook<
  InternalArgs,
  CauseRegisterResponseRow[],
  GetCausesQuery
>({
  trpcQueryOptions: (trpc, { entityIds }) =>
    trpc.frontend.cause.register.queryOptions({ entityIds }),
  mapTrpcDataToGraphQL: mapTrpcCausesToGraphQL,
  graphqlDocument: GetCausesDocument,
  graphqlVariables: useCausesGraphqlVariables,
});

/**
 * Fetches causes with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses useEntityFilter context internally — consumers
 * don't need to pass entityIds.
 */
export const useGetCauseRegister = (params: {
  queryArgs: UseGetCauseRegisterArgs;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetCauseRegisterBase({
    queryArgs: {
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};
